import { createRouter as _createRouter, createWebHistory } from "vue-router";
import { useThemeContext } from "@/core/composables";
import { XApiPermissions } from "@/core/enums";
import { getReturnUrlValue } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { useKeycloakSignIn } from "@/shared/sign-in/composables/useKeycloakSignIn";
import { mainRoutes } from "./routes";
import type { RouteRecordName } from "vue-router";

const PASSWORD_AUTHENTICATION_TYPE = "Password";

export function createRouter(options: { base: string }) {
  const { base } = options;
  const { isAuthenticated, organization, user, checkPermissions } = useUser();
  const { themeContext } = useThemeContext();
  const { signIn: signInWithKeycloak } = useKeycloakSignIn();

  const router = _createRouter({
    routes: mainRoutes,
    history: createWebHistory(base),
    scrollBehavior(to, from, savedPosition) {
      if (to.path !== from.path) {
        return savedPosition || { top: 0 };
      } else {
        return undefined;
      }
    },
  });

  router.beforeEach((to, _from, next) => {
    // Protecting routes
    const unauthorizedAccessIsDenied: boolean =
      !isAuthenticated.value &&
      !to.meta.public &&
      (to.meta.requiresAuth || !themeContext.value.storeSettings.anonymousUsersAllowed);
      
    if (unauthorizedAccessIsDenied) {
      const authenticationTypes: string[] = themeContext.value.storeSettings?.authenticationTypes?.length
        ? (themeContext.value.storeSettings.authenticationTypes as string[])
        : [PASSWORD_AUTHENTICATION_TYPE];
      const useKeycloak = !authenticationTypes.includes(PASSWORD_AUTHENTICATION_TYPE);

      signInWithKeycloak(to.fullPath, useKeycloak);

      if (useKeycloak) {
        // navigation is handled by the external Keycloak redirect
        return next(false);
      }

      return next({
        name: "SignIn",
        // save the location we were at to come back later
        query: { returnUrl: to.fullPath },
      });
    }

    // Protecting company routes
    if (to.meta.requiresOrganization && !organization.value) {
      return next({ name: "Account" });
    }

    // Make Dashboard the default Home page for authorized users
    if (
      isAuthenticated.value &&
      Array<RouteRecordName>(
        "SignIn",
        "SignUp",
        "ForgotPassword",
        "ResetPassword",
        "SetPassword",
        "ConfirmInvitation",
      ).includes(to.name!)
    ) {
      return next(getReturnUrlValue() || { name: "Home" });
    }

    return next();
  });

  return router;
}
